import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAfterPaintEffect, useAppSelector } from './app/hooks';
import { useGetEventsQuery, useGetFilterOptionsQuery } from './features/api/api';
import { applyFilters, filterTypes } from './helpers/filter-helpers';
import { Month, CombinedFilterData } from './helpers/common-types';
import { CalendarLayoutA, CalendarLayoutB, CalendarLayoutC } from './components/calendar-layout-options';
import { DATE_FORMAT } from './helpers/date-helpers';
import { useMediaQuery } from 'react-responsive';
import { Promo } from './components/promo';
import { ScrollToTop } from './components/scroll-to-top';
import { Loader } from './components/loader';
import { debounce } from 'lodash';
import { useAppDispatch } from './app/store';
import { setFilterBarHeight, setAppendMonths, setSingleMonth, setCurrentDate } from './features/calendar/calendar-slice';

function App() {
    const [layoutSetting, setLayoutSetting] = useState('A');
    const { calendarAppHeading, lastDate, productionSeasonId, gridViewModeButtonText, mainBody, orderOfVenueForEvents } = useAppSelector(state => state.settings);
    const currentRange = useAppSelector(state => state.calendar.currentRange);
    const currentAppliedFilters = useAppSelector(state => state.calendar.currentAppliedFilters);
    const today = useAppSelector(state => state.calendar.todaysDate);
    const promoCode = useAppSelector(state => state.settings.promoCode);
    const firstLoad = useRef(true);

    const { data: combinedFilterData = {}, isFetching: filtersFetching } = useGetFilterOptionsQuery({ startDate: today, endDate: lastDate, filterNames: filterTypes, promoCode: promoCode });
    const { data: events = [], isFetching, isLoading } = useGetEventsQuery({ ...currentRange, productionSeasonId: productionSeasonId });
    const filteredEvents = applyFilters(events, currentAppliedFilters, orderOfVenueForEvents);

    useEffect(() => {
        if (!firstLoad.current && window.location.href.indexOf('?') > -1) {
            // clear the query string when the range has changed
            window.history.pushState({ page: location.pathname }, document.title, window.location.pathname);
        }
        firstLoad.current = false;
    }, [firstLoad, currentRange]);

    return (
        <div className="ace-calendar stack-xl">
            <div className="ace-calendar-layout stack">
                <h1>{calendarAppHeading}</h1>
                {
                    mainBody &&
                    <div className="rich-text" dangerouslySetInnerHTML={{ __html: mainBody }} />
                }
                <div className="ace-layout ace-layout-9-3">
                    <div className="spaced-list space-top-lg">
                        <button className="btn btn-dashed" onClick={() => setLayoutSetting('A')} disabled={layoutSetting == 'A'}>Mini-Calendar</button>
                        <button className="btn btn-dashed" onClick={() => setLayoutSetting('C')} disabled={layoutSetting == 'C'}>{gridViewModeButtonText}</button>
                    </div>
                </div>
            </div>
            {
                isLoading ? <Loader /> :
                    !!filteredEvents && !!combinedFilterData ?
                        <CalendarLayout {...{ filteredEvents, layoutSetting, combinedFilterData }} />
                        : null
            }
            <ScrollToTop />
        </div>
    )
}

export default App

const CalendarLayout = (props: { filteredEvents: Month[], layoutSetting: string, combinedFilterData: CombinedFilterData }) => {
    const { layoutSetting, ...layoutProps } = props;
    const dispatch = useAppDispatch();
    const layoutBreakpoint = useAppSelector(state => state.settings.layoutBreakpoint);
    const appendMonths = useAppSelector(state => state.calendar.appendMonths);
    const gridBreakpoint = useMediaQuery({ query: `(max-width: ${layoutBreakpoint})` });
    const filterBarRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        }
    }, []);

    useAfterPaintEffect(() => {
        setFilterHeight();
    }, [layoutSetting]);

    const setFilterHeight = () => {
        const filterHeight = filterBarRef?.current?.offsetHeight;
        if (filterHeight) {
            dispatch(setFilterBarHeight(filterHeight));
            document.documentElement.style.setProperty('--filter-bar-height', `${filterHeight}px`);
        }
    }

    // Set the type of range for this layout
    useLayoutEffect(() => {
        if (appendMonths) {
            if (layoutSetting == 'A') {
                dispatch(setSingleMonth());
            }
        }
        else {
            if (layoutSetting != 'A') {
                dispatch(setAppendMonths());
            }
        }
    }, [layoutSetting]);

    const handleResize = debounce(setFilterHeight, 250);

    const getLayout = (layout: string) => {
        switch (layout) {
            case 'A':
                // Layout A is special in that it shows one month at a time. Wait until the store settings have caught up before rendering this view.
                // If the component loads too early after switching from a multi-month view, it loads with all months initially and the scroll tracking gets out of sync.
                if (!appendMonths && layoutProps.filteredEvents.length == 1) {
                    return <CalendarLayoutA {...layoutProps} ref={filterBarRef} />
                }
                else {
                    return <Loader />
                }
            case 'B':
                return <CalendarLayoutB {...layoutProps} ref={filterBarRef} />
            case 'C': {
                if (gridBreakpoint) {
                    return <CalendarLayoutB {...layoutProps} ref={filterBarRef} />
                }
                else {
                    return <CalendarLayoutC {...layoutProps} ref={filterBarRef} />
                }
            }
            default:
                return <Loader />
        }
    }
    return getLayout(layoutSetting);
}